import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Brain,
  TrendingUp,
  Target,
  BarChart3,
  Lock,
  Sparkles,
  Activity,
  ChevronRight,
  RefreshCw,
  Loader2,
  Crown,
  LineChart,
  PieChart,
  Zap,
  Trophy,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Percent,
  MessageSquare,
  Send,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sport icons by league type
const SPORT_ICONS = {
  basketball: '🏀',
  football: '🏈',
  baseball: '⚾',
  hockey: '🏒',
  soccer: '⚽',
  default: '🎯'
};

// Get icon based on sport type
const getLeagueIcon = (sport) => {
  const sportLower = (sport || '').toLowerCase();
  return SPORT_ICONS[sportLower] || SPORT_ICONS.default;
};

export default function Analysis() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState('nba');
  const [activeTab, setActiveTab] = useState('platform');
  const [loading, setLoading] = useState(true);
  
  // Dynamic leagues from API
  const [leagues, setLeagues] = useState([]);
  
  // Dynamic pricing from admin settings
  const [pricing, setPricing] = useState({ subscription_price: 20, currency: 'CAD' });
  
  // Data states
  const [platformAnalysis, setPlatformAnalysis] = useState(null);
  const [leagueAnalysis, setLeagueAnalysis] = useState(null);
  const [trends, setTrends] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamAnalysis, setTeamAnalysis] = useState(null);
  const [teams, setTeams] = useState([]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // Fetch pricing and leagues on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [pricingRes, leaguesRes] = await Promise.all([
          fetch(`${API_URL}/api/pricing/public`),
          fetch(`${API_URL}/api/leagues`)
        ]);
        
        if (pricingRes.ok) {
          const data = await pricingRes.json();
          setPricing(data);
        }
        if (leaguesRes.ok) {
          const data = await leaguesRes.json();
          setLeagues(data.map(l => ({
            id: l.league_id,
            name: l.abbreviation || l.name,
            fullName: l.name,
            icon: getLeagueIcon(l.sport)
          })));
        }
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      }
    };
    fetchInitialData();
  }, []);
  
  // Chat states
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // Function to send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/analysis/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question: userMessage,
          context_type: 'general',
          context_data: { league_id: selectedLeague }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I could not process your question. Please try again.' }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    const checkSubscriber = async () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        
        // Check subscription status from API (in case localStorage is stale)
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const freshUser = await response.json();
            // Admin users always have full access, OR active subscribers
            const isSub = freshUser.is_admin || 
                         freshUser.is_subscriber ||
                         freshUser.subscription_status === 'active' ||
                         freshUser.subscription_tier === 'premium' ||
                         freshUser.subscription_tier === 'pro';
            setIsSubscriber(isSub);
            // Update localStorage with fresh data
            localStorage.setItem('user', JSON.stringify(freshUser));
            setUser(freshUser);
          } else {
            // Fallback to localStorage
            setIsSubscriber(userData.is_subscriber || userData.is_admin);
          }
        } catch (err) {
          console.error('Failed to check subscription:', err);
          setIsSubscriber(userData.is_subscriber || userData.is_admin);
        }
      }
    };
    checkSubscriber();
  }, []);

  const fetchPlatformAnalysis = useCallback(async () => {
    if (!isSubscriber) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analysis/platform`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPlatformAnalysis(data);
      } else if (response.status === 403) {
        setIsSubscriber(false);
      }
    } catch (err) {
      console.error('Failed to fetch platform analysis:', err);
    } finally {
      setLoading(false);
    }
  }, [isSubscriber]);

  const fetchLeagueAnalysis = useCallback(async () => {
    if (!isSubscriber) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analysis/league/${selectedLeague}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLeagueAnalysis(data);
      }
    } catch (err) {
      console.error('Failed to fetch league analysis:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedLeague, isSubscriber]);

  const fetchTrends = useCallback(async () => {
    if (!isSubscriber) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analysis/trends/${selectedLeague}?days=30`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTrends(data);
      }
    } catch (err) {
      console.error('Failed to fetch trends:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedLeague, isSubscriber]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats/teams/${selectedLeague}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data.teams || []);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [selectedLeague]);

  const fetchTeamAnalysis = async (teamId) => {
    if (!isSubscriber) return;
    setSelectedTeamId(teamId);
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analysis/team/${selectedLeague}/${teamId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeamAnalysis(data);
      }
    } catch (err) {
      console.error('Failed to fetch team analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSubscriber) return;
    
    // PERFORMANCE: Use parallel fetching where multiple calls are needed
    if (activeTab === 'platform') {
      fetchPlatformAnalysis();
    } else if (activeTab === 'ai') {
      // Fetch league analysis and teams in parallel
      Promise.all([fetchLeagueAnalysis(), fetchTeams()]);
    } else if (activeTab === 'trends') {
      fetchTrends();
    } else if (activeTab === 'teams') {
      fetchTeams();
    }
  }, [activeTab, selectedLeague, isSubscriber, fetchPlatformAnalysis, fetchLeagueAnalysis, fetchTrends, fetchTeams]);

  // Non-subscriber view - Shows feature preview with upgrade prompt
  if (!isSubscriber) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        
        <main className="flex-1 container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-purple-500/30">
              <Brain className="w-10 h-10 text-purple-400" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Platform Analysis</h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Unlock advanced AI-powered analytics exclusively for subscribers
            </p>
          </div>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            {/* AI Analysis */}
            <Card className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20 hover:border-purple-500/40 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-3">
                  <Brain className="w-6 h-6 text-purple-400" />
                </div>
                <CardTitle className="text-xl">AI Analysis</CardTitle>
                <CardDescription>Deep insights powered by GPT-5.2</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" /> AI-generated performance insights</li>
                  <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" /> Smart recommendations</li>
                  <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" /> Betting pattern analysis</li>
                </ul>
              </CardContent>
            </Card>

            {/* Trend Analysis */}
            <Card className="bg-gradient-to-br from-green-500/5 to-emerald-500/5 border-green-500/20 hover:border-green-500/40 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-3">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <CardTitle className="text-xl">Trend Analysis</CardTitle>
                <CardDescription>Track accuracy trends over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><LineChart className="w-4 h-4 text-green-400" /> Historical prediction charts</li>
                  <li className="flex items-center gap-2"><LineChart className="w-4 h-4 text-green-400" /> Daily/weekly accuracy trends</li>
                  <li className="flex items-center gap-2"><LineChart className="w-4 h-4 text-green-400" /> Performance patterns</li>
                </ul>
              </CardContent>
            </Card>

            {/* Team Analytics */}
            <Card className="bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border-blue-500/20 hover:border-blue-500/40 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-3">
                  <Target className="w-6 h-6 text-blue-400" />
                </div>
                <CardTitle className="text-xl">Team Analytics</CardTitle>
                <CardDescription>Detailed team performance analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><Trophy className="w-4 h-4 text-blue-400" /> Win rate & performance stats</li>
                  <li className="flex items-center gap-2"><Trophy className="w-4 h-4 text-blue-400" /> Home vs away analysis</li>
                  <li className="flex items-center gap-2"><Trophy className="w-4 h-4 text-blue-400" /> Team-specific AI insights</li>
                </ul>
              </CardContent>
            </Card>

            {/* Platform Stats */}
            <Card className="bg-gradient-to-br from-yellow-500/5 to-orange-500/5 border-yellow-500/20 hover:border-yellow-500/40 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mb-3">
                  <BarChart3 className="w-6 h-6 text-yellow-400" />
                </div>
                <CardTitle className="text-xl">Platform Stats</CardTitle>
                <CardDescription>Overall prediction accuracy metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><Percent className="w-4 h-4 text-yellow-400" /> League-by-league accuracy</li>
                  <li className="flex items-center gap-2"><Percent className="w-4 h-4 text-yellow-400" /> Confidence level breakdown</li>
                  <li className="flex items-center gap-2"><Percent className="w-4 h-4 text-yellow-400" /> Real-time platform metrics</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <Card className="max-w-2xl mx-auto bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10 border-purple-500/30">
            <CardContent className="py-8 text-center">
              <Lock className="w-12 h-12 mx-auto mb-4 text-purple-400" />
              <h2 className="text-2xl font-bold mb-2">Unlock Full Access</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Subscribe now to access all analytics features and gain a competitive edge with AI-powered insights.
              </p>
              <Button 
                size="lg" 
                onClick={() => navigate('/billing')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                data-testid="upgrade-premium-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Upgrade to Premium
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                Starting at ${pricing.subscription_price || 20}/{pricing.currency || 'CAD'}/month • Cancel anytime
              </p>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
              <Brain className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" data-testid="analysis-page-title">Platform Analysis</h1>
              <p className="text-muted-foreground">Advanced analytics powered by AI</p>
            </div>
            <Badge className="bg-purple-500/20 text-purple-400 ml-2">
              <Crown className="w-3 h-3 mr-1" />
              Premium
            </Badge>
          </div>
          
          <Select value={selectedLeague} onValueChange={setSelectedLeague}>
            <SelectTrigger className="w-[200px] mt-4 md:mt-0" data-testid="league-selector">
              <SelectValue placeholder="Select League" />
            </SelectTrigger>
            <SelectContent>
              {leagues.map(league => (
                <SelectItem key={league.id} value={league.id}>
                  <span className="mr-2">{league.icon}</span>
                  {league.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-12">
            <TabsTrigger value="platform" className="flex items-center gap-2" data-testid="tab-platform">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Platform Stats</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-2" data-testid="tab-trends">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Trend Analysis</span>
              <span className="sm:hidden">Trends</span>
            </TabsTrigger>
            <TabsTrigger value="teams" className="flex items-center gap-2" data-testid="tab-teams">
              <Target className="w-4 h-4" />
              <span className="hidden sm:inline">Team Analytics</span>
              <span className="sm:hidden">Teams</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2" data-testid="tab-ai">
              <Brain className="w-4 h-4" />
              <span className="hidden sm:inline">AI Analysis</span>
              <span className="sm:hidden">AI</span>
            </TabsTrigger>
          </TabsList>

          {/* Platform Stats Tab */}
          <TabsContent value="platform" data-testid="platform-content">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : platformAnalysis ? (
              <div className="space-y-6">
                {/* Key Metrics Overview */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-2">
                        <Activity className="w-5 h-5 text-blue-400" />
                      </div>
                      <p className="text-sm text-muted-foreground">Total Games</p>
                      <p className="text-3xl font-bold text-blue-400">
                        {platformAnalysis.platform_stats?.total_games?.toLocaleString() || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-2">
                        <Percent className="w-5 h-5 text-green-400" />
                        {platformAnalysis.platform_stats?.overall_accuracy >= 55 ? (
                          <ArrowUpRight className="w-4 h-4 text-green-400" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">Overall Accuracy</p>
                      <p className="text-3xl font-bold text-green-400">
                        {platformAnalysis.platform_stats?.overall_accuracy || 0}%
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-2">
                        <Trophy className="w-5 h-5 text-purple-400" />
                      </div>
                      <p className="text-sm text-muted-foreground">Games Evaluated</p>
                      <p className="text-3xl font-bold text-purple-400">
                        {platformAnalysis.platform_stats?.total_evaluated?.toLocaleString() || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-2">
                        <Target className="w-5 h-5 text-yellow-400" />
                      </div>
                      <p className="text-sm text-muted-foreground">Correct Predictions</p>
                      <p className="text-3xl font-bold text-yellow-400">
                        {platformAnalysis.platform_stats?.total_correct?.toLocaleString() || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Interactive Q&A Chat */}
                <Card className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border-blue-500/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-400" />
                      Ask AI Analyst
                    </CardTitle>
                    <CardDescription>Ask questions about predictions, teams, patterns, and strategies</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Chat messages */}
                      <div className="max-h-64 overflow-y-auto space-y-3 p-3 bg-background/50 rounded-lg">
                        {chatMessages.length === 0 ? (
                          <p className="text-muted-foreground text-sm text-center py-4">
                            Ask about prediction patterns, team comparisons, or betting strategies...
                          </p>
                        ) : (
                          chatMessages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
                                msg.role === 'user' 
                                  ? 'bg-primary text-primary-foreground' 
                                  : 'bg-secondary'
                              }`}>
                                {msg.content}
                              </div>
                            </div>
                          ))
                        )}
                        {chatLoading && (
                          <div className="flex justify-start">
                            <div className="bg-secondary p-3 rounded-lg">
                              <Loader2 className="w-4 h-4 animate-spin" />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Chat input */}
                      <div className="flex gap-2">
                        <Input
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          placeholder="Ask about predictions, teams, patterns..."
                          onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                          className="flex-1"
                          data-testid="analysis-chat-input"
                        />
                        <Button onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()}>
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Accuracy by League - Visual Bar Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-green-400" />
                      Accuracy by League
                    </CardTitle>
                    <CardDescription>Platform prediction accuracy across all leagues</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {platformAnalysis.accuracy_by_league?.map((league, idx) => {
                        const accuracy = league.accuracy || 0;
                        const isHot = accuracy >= 60;
                        const isGood = accuracy >= 50 && accuracy < 60;
                        return (
                          <div key={idx} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-bold uppercase text-sm">{league.league_id}</span>
                                {isHot && <Badge className="bg-green-500 text-white text-xs">HOT</Badge>}
                              </div>
                              <div className="flex items-center gap-3 text-sm">
                                <span className="text-muted-foreground">
                                  {league.correct}/{league.total_games}
                                </span>
                                <span className={`font-bold ${isHot ? 'text-green-400' : isGood ? 'text-yellow-400' : 'text-red-400'}`}>
                                  {accuracy}%
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-secondary/50 rounded-full h-3 overflow-hidden">
                              <div 
                                className={`h-3 rounded-full transition-all duration-500 ${
                                  isHot ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                                  isGood ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
                                  'bg-gradient-to-r from-red-500 to-orange-500'
                                }`}
                                style={{ width: `${accuracy}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* AI Summary */}
                {platformAnalysis.ai_summary && (
                  <Card className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                        AI Platform Analysis
                      </CardTitle>
                      <CardDescription>GPT-5.2 powered insights</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans leading-relaxed">
                        {platformAnalysis.ai_summary}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No platform stats available</p>
                <Button variant="outline" onClick={fetchPlatformAnalysis} className="mt-4">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Load Stats
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Trends Tab - Historical Prediction Performance Charts */}
          <TabsContent value="trends" data-testid="trends-content">
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : trends ? (
              <div className="space-y-6">
                {/* Trend Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground mb-2">Trend Direction</p>
                      <Badge className={`text-lg px-4 py-2 ${
                        trends.trend_direction === 'improving' ? 'bg-green-500/20 text-green-400' :
                        trends.trend_direction === 'declining' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {trends.trend_direction === 'improving' && <TrendingUp className="w-4 h-4 mr-2 inline" />}
                        {trends.trend_direction === 'declining' && <ArrowDownRight className="w-4 h-4 mr-2 inline" />}
                        {trends.trend_direction?.replace('_', ' ').toUpperCase() || 'ANALYZING'}
                      </Badge>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground mb-2">Games Analyzed</p>
                      <p className="text-3xl font-bold">{trends.summary?.total_games || 0}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground mb-2">Avg Accuracy</p>
                      <p className="text-3xl font-bold text-green-400">{trends.summary?.average_accuracy || 0}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Historical Accuracy Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <LineChart className="w-5 h-5 text-blue-400" />
                      Historical Prediction Performance
                    </CardTitle>
                    <CardDescription>
                      {selectedLeague.toUpperCase()} accuracy over the last 30 days
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {trends.daily_data && trends.daily_data.length > 0 ? (
                      <div className="space-y-1">
                        {/* Visual bar chart representation */}
                        <div className="flex items-end gap-1 h-40 mb-4">
                          {trends.daily_data.map((day, idx) => {
                            const height = Math.max(day.accuracy * 1.5, 10);
                            const isHot = day.accuracy >= 60;
                            const isGood = day.accuracy >= 50;
                            return (
                              <div 
                                key={idx}
                                className="flex-1 flex flex-col items-center justify-end group relative"
                              >
                                <div 
                                  className={`w-full rounded-t transition-all cursor-pointer hover:opacity-80 ${
                                    isHot ? 'bg-green-500' : isGood ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ height: `${height}px` }}
                                  title={`${day.date}: ${day.accuracy}% (${day.correct}/${day.games})`}
                                />
                                {/* Tooltip on hover */}
                                <div className="absolute bottom-full mb-2 hidden group-hover:block bg-card border border-border rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                                  <p className="font-bold">{day.date}</p>
                                  <p>{day.accuracy}% ({day.correct}/{day.games})</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Legend and date range */}
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border">
                          <span>{trends.daily_data[0]?.date}</span>
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-green-500 rounded" />
                              60%+
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-yellow-500 rounded" />
                              50-59%
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-red-500 rounded" />
                              &lt;50%
                            </span>
                          </div>
                          <span>{trends.daily_data[trends.daily_data.length - 1]?.date}</span>
                        </div>
                        
                        {/* Detailed data table */}
                        <div className="mt-6 max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-background">
                              <tr className="border-b border-border">
                                <th className="text-left py-2 font-medium">Date</th>
                                <th className="text-center py-2 font-medium">Games</th>
                                <th className="text-center py-2 font-medium">Correct</th>
                                <th className="text-right py-2 font-medium">Accuracy</th>
                              </tr>
                            </thead>
                            <tbody>
                              {trends.daily_data.slice().reverse().map((day, idx) => (
                                <tr key={idx} className="border-b border-border/50 hover:bg-secondary/30">
                                  <td className="py-2">{day.date}</td>
                                  <td className="text-center py-2">{day.games}</td>
                                  <td className="text-center py-2">{day.correct}</td>
                                  <td className="text-right py-2">
                                    <span className={`font-bold ${
                                      day.accuracy >= 60 ? 'text-green-400' :
                                      day.accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'
                                    }`}>
                                      {day.accuracy}%
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No trend data available for {selectedLeague.toUpperCase()}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12">
                <LineChart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">Select a league to view trends</p>
              </div>
            )}
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams" data-testid="teams-content">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Team List */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-400" />
                    Select Team
                  </CardTitle>
                  <CardDescription>Click a team for AI analysis</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                    {teams.length > 0 ? teams.map(team => (
                      <div
                        key={team.team_id}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedTeamId === team.team_id 
                            ? 'bg-primary/20 border-2 border-primary/50' 
                            : 'bg-secondary/30 hover:bg-secondary/50 border-2 border-transparent'
                        }`}
                        onClick={() => fetchTeamAnalysis(team.team_id)}
                        data-testid={`team-${team.team_id}`}
                      >
                        <div className="flex items-center gap-3">
                          {team.logo && (
                            <img src={team.logo} alt="" className="w-8 h-8 object-contain" />
                          )}
                          <span className="font-medium flex-1 truncate">{team.name}</span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No teams available</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Team Analysis */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {teamAnalysis?.team_name ? (
                      <>
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        {teamAnalysis.team_name}
                      </>
                    ) : (
                      <>
                        <Brain className="w-5 h-5 text-purple-400" />
                        Team Analysis
                      </>
                    )}
                  </CardTitle>
                  {teamAnalysis && (
                    <CardDescription>
                      AI-powered performance insights
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {loading && selectedTeamId ? (
                    <div className="space-y-4">
                      <Skeleton className="h-20 w-full" />
                      <Skeleton className="h-40 w-full" />
                    </div>
                  ) : teamAnalysis ? (
                    <div className="space-y-6">
                      {/* Team Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-lg text-center border border-green-500/20">
                          <p className="text-xs text-muted-foreground mb-1">Win Rate</p>
                          <p className="text-2xl font-bold text-green-400">{teamAnalysis.stats?.win_rate || 0}%</p>
                        </div>
                        <div className="p-4 bg-secondary/30 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground mb-1">Record</p>
                          <p className="text-2xl font-bold">
                            {teamAnalysis.stats?.wins || 0}-{(teamAnalysis.stats?.final_games || 0) - (teamAnalysis.stats?.wins || 0)}
                          </p>
                        </div>
                        <div className="p-4 bg-secondary/30 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground mb-1">Home Wins</p>
                          <p className="text-2xl font-bold">{teamAnalysis.stats?.home_wins || 0}</p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg text-center border border-purple-500/20">
                          <p className="text-xs text-muted-foreground mb-1">Our Accuracy</p>
                          <p className="text-2xl font-bold text-purple-400">{teamAnalysis.stats?.prediction_accuracy || 0}%</p>
                        </div>
                      </div>

                      {/* AI Analysis */}
                      {teamAnalysis.ai_analysis && (
                        <div className="p-4 bg-gradient-to-br from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-purple-400" />
                            <span className="font-medium text-purple-400">AI Analysis</span>
                            <Badge className="bg-purple-500/20 text-purple-400 text-xs">GPT-5.2</Badge>
                          </div>
                          <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans leading-relaxed">
                            {teamAnalysis.ai_analysis}
                          </pre>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16 text-muted-foreground">
                      <Target className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium mb-1">Select a team</p>
                      <p className="text-sm">Choose a team from the list to view AI-powered analysis</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AI Analysis Tab - League-wide AI Analysis */}
          <TabsContent value="ai" data-testid="ai-content">
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : leagueAnalysis ? (
              <div className="space-y-6">
                {/* League Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground">Total Games</p>
                      <p className="text-2xl font-bold">{leagueAnalysis.stats?.total_games || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground">Completed</p>
                      <p className="text-2xl font-bold">{leagueAnalysis.stats?.final_games || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground">Prediction Accuracy</p>
                      <p className="text-2xl font-bold text-green-400">{leagueAnalysis.stats?.prediction_accuracy || 0}%</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground">Scheduled</p>
                      <p className="text-2xl font-bold">{leagueAnalysis.stats?.scheduled_games || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Teams */}
                {leagueAnalysis.top_teams?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        Top Teams by Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {leagueAnalysis.top_teams.slice(0, 10).map((team, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                            <span className="font-medium truncate">{team.name}</span>
                            <Badge variant="outline">{team.games}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AI League Analysis */}
                {leagueAnalysis.ai_analysis && (
                  <Card className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-400" />
                        AI League Analysis - {selectedLeague.toUpperCase()}
                      </CardTitle>
                      <CardDescription>
                        <Badge className="bg-purple-500/20 text-purple-400">Powered by GPT-5.2</Badge>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans leading-relaxed">
                        {leagueAnalysis.ai_analysis}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground mb-4">No AI analysis available</p>
                <Button variant="outline" onClick={fetchLeagueAnalysis}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Generate Analysis
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
